import { useState, useEffect } from 'react';
import { Brain, CheckSquare, Square, Loader2, AlertCircle, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import exploreApi from '../../exploreApi';

export default function BulkMetadataGenerator({ sourceId, sourceName, tables, onComplete }) {
  const [selectedTables, setSelectedTables] = useState(new Set());
  const [isGenerating, setIsGenerating] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const [error, setError] = useState(null);
  const [skipExisting, setSkipExisting] = useState(true);
  const [maxParallel, setMaxParallel] = useState(5);
  const [tablesWithMetadata, setTablesWithMetadata] = useState(new Set());
  const [checkingMetadata, setCheckingMetadata] = useState(false);
  const [filterDb, setFilterDb] = useState('all');
  const [filterSchema, setFilterSchema] = useState('all');

  // Derive databases and schemas from table paths (format: db.schema.table or schema.table)
  const parsePath = (path) => {
    const parts = path.split('.');
    if (parts.length >= 3) return { db: parts[0], schema: parts[1] };
    if (parts.length === 2) return { db: 'default', schema: parts[0] };
    return { db: 'default', schema: 'default' };
  };

  const databases = ['all', ...new Set(tables.map(t => parsePath(t.path).db))];
  const schemas = ['all', ...new Set(
    tables
      .filter(t => filterDb === 'all' || parsePath(t.path).db === filterDb)
      .map(t => parsePath(t.path).schema)
  )];

  const filteredTables = tables.filter(t => {
    const { db, schema } = parsePath(t.path);
    return (filterDb === 'all' || db === filterDb) && (filterSchema === 'all' || schema === filterSchema);
  });

  useEffect(() => {
    if (tables.length > 0 && tables.length <= 50) {
      const checkAll = async () => {
        setCheckingMetadata(true);
        const withMeta = new Set();
        for (const t of tables) {
          try {
            const r = await exploreApi.checkTableMetadata(sourceId, t.path);
            if (r.has_metadata) withMeta.add(t.path);
          } catch { /* silent */ }
        }
        setTablesWithMetadata(withMeta);
        setCheckingMetadata(false);
      };
      checkAll();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId, tables.length]);

  useEffect(() => {
    if (!jobStatus?.job_id || ['completed', 'failed'].includes(jobStatus.status)) return;
    const interval = setInterval(async () => {
      try {
        const status = await exploreApi.getBulkMetadataStatus(jobStatus.job_id);
        setJobStatus(status);
        if (['completed', 'failed'].includes(status.status)) {
          setIsGenerating(false);
          if (status.status === 'completed' && onComplete) onComplete();
        }
      } catch (err) {
        console.error('Failed to poll job status:', err);
      }
    }, 3000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobStatus?.job_id, jobStatus?.status]);

  const toggleTable = (path) => {
    setSelectedTables(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedTables.size === tables.length) {
      setSelectedTables(new Set());
    } else {
      setSelectedTables(new Set(tables.map(t => t.path)));
    }
  };

  const handleGenerate = async () => {
    if (selectedTables.size === 0) { setError('Select at least one table'); return; }
    setIsGenerating(true);
    setError(null);
    setJobStatus(null);
    try {
      const selected = tables.filter(t => selectedTables.has(t.path));
      const resp = await exploreApi.bulkGenerateMetadata({
        tables: selected.map(t => ({ source_id: sourceId, table_path: t.path })),
        skip_existing: skipExisting,
        max_parallel: maxParallel,
      });
      if (resp.job_id) {
        setJobStatus({ job_id: resp.job_id, status: 'running', total_tables: selected.length, completed_tables: 0, failed_tables: 0, progress_percentage: 0, results: [] });
      } else {
        setIsGenerating(false);
        setError('Failed to start job');
      }
    } catch (err) {
      setIsGenerating(false);
      setError(err.message || 'Failed to start');
    }
  };

  const allSelected = selectedTables.size === tables.length && tables.length > 0;

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-5 py-4 bg-gradient-to-r from-gray-50 to-blue-50 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-primary" />
          <div>
            <p className="text-sm font-semibold text-gray-900">AI Metadata Generator</p>
            <p className="text-xs text-gray-500">{sourceName} · {tables.length} tables</p>
          </div>
        </div>
        {checkingMetadata && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking metadata…
          </div>
        )}
      </div>

      <div className="p-5 space-y-4">
        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3">
            <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
            <p className="text-xs text-red-700">{error}</p>
          </div>
        )}

        {/* Job progress */}
        {jobStatus && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-gray-900 capitalize">{jobStatus.status}</span>
              <span className="text-gray-500">{jobStatus.completed_tables}/{jobStatus.total_tables}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-primary h-2 rounded-full transition-all duration-500" style={{ width: `${jobStatus.progress_percentage || 0}%` }} />
            </div>
            {jobStatus.results?.length > 0 && (
              <div className="max-h-32 overflow-y-auto space-y-1">
                {jobStatus.results.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    {r.success ? <CheckCircle className="h-3.5 w-3.5 text-green-500" /> : r.skipped ? <RefreshCw className="h-3.5 w-3.5 text-gray-400" /> : <XCircle className="h-3.5 w-3.5 text-red-500" />}
                    <span className="text-gray-600 truncate">{r.table_path}</span>
                    {r.error && <span className="text-red-500 truncate">— {r.error}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Database / Schema filter */}
        {!isGenerating && (
          <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm">
            <span className="text-gray-500 font-medium flex-shrink-0">Filter:</span>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500">Database</label>
              <select
                value={filterDb}
                onChange={e => { setFilterDb(e.target.value); setFilterSchema('all'); }}
                className="border border-gray-300 rounded px-2 py-1 text-xs bg-white"
              >
                {databases.map(db => <option key={db} value={db}>{db === 'all' ? 'All databases' : db}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500">Schema</label>
              <select
                value={filterSchema}
                onChange={e => setFilterSchema(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-xs bg-white"
              >
                {schemas.map(s => <option key={s} value={s}>{s === 'all' ? 'All schemas' : s}</option>)}
              </select>
            </div>
            <span className="text-xs text-gray-400 ml-auto">{filteredTables.length} of {tables.length} tables</span>
          </div>
        )}

        {/* Settings */}
        {!isGenerating && (
          <div className="flex items-center gap-6 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={skipExisting} onChange={e => setSkipExisting(e.target.checked)} className="rounded" />
              <span className="text-gray-600">Skip tables with existing metadata</span>
            </label>
            <label className="flex items-center gap-2">
              <span className="text-gray-600">Parallel:</span>
              <select value={maxParallel} onChange={e => setMaxParallel(Number(e.target.value))} className="border border-gray-300 rounded px-2 py-0.5 text-xs bg-white">
                <option value={1}>1 (Sequential)</option>
                <option value={3}>3 tables</option>
                <option value={5}>5 tables</option>
                <option value={10}>10 tables</option>
              </select>
            </label>
          </div>
        )}

        {/* Table list */}
        {!isGenerating && (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
              <button onClick={toggleAll} className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900">
                {allSelected ? <CheckSquare className="h-4 w-4 text-primary" /> : <Square className="h-4 w-4" />}
                {allSelected ? 'Deselect all' : 'Select all'}
              </button>
              {(() => {
                const allVisibleSelected = filteredTables.length > 0 && filteredTables.every(t => selectedTables.has(t.path));
                return (
                  <button
                    onClick={() => {
                      const next = new Set(selectedTables);
                      if (allVisibleSelected) {
                        filteredTables.forEach(t => next.delete(t.path));
                      } else {
                        filteredTables.forEach(t => next.add(t.path));
                      }
                      setSelectedTables(next);
                    }}
                    className="text-xs text-primary hover:underline"
                  >
                    {allVisibleSelected ? `Deselect visible (${filteredTables.length})` : `Select visible (${filteredTables.length})`}
                  </button>
                );
              })()}
              <span className="text-xs text-gray-400 ml-auto">{selectedTables.size} selected</span>
            </div>
            <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
              {filteredTables.map(t => (
                <div key={t.path} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer" onClick={() => toggleTable(t.path)}>
                  {selectedTables.has(t.path) ? <CheckSquare className="h-4 w-4 text-primary flex-shrink-0" /> : <Square className="h-4 w-4 text-gray-300 flex-shrink-0" />}
                  <span className="text-xs text-gray-700 flex-1 truncate font-mono">{t.name}</span>
                  {tablesWithMetadata.has(t.path) && <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded">has metadata</span>}
                  {t.rowCount != null && <span className="text-[10px] text-gray-400">{t.rowCount.toLocaleString()} rows</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          onClick={handleGenerate}
          disabled={isGenerating || selectedTables.size === 0}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primaryDark disabled:opacity-50 transition-colors"
        >
          {isGenerating ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating…</> : <> Generate Metadata for {selectedTables.size} table{selectedTables.size !== 1 ? 's' : ''}</>}
        </button>
      </div>
    </div>
  );
}
